import React, { Component, Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';

import RouteGuard from './core/component/router/route-guard';
// import AppAdminGuard from './guard/app-admin.guard';
import AppGuard from './guard/app.guard';
import AdminGuard from './guard/admin.guard';

import APP_CONST from './const/app-const';
import ADMIN_CONST from './const/admin-const';
import App from './app/poc/Index';
import RequireAppAdmin from './app/component/view/require-app-admin';

/** Legacy URLs (`/consignmentnotes/...`) → quotation-style short segment `/csns/...`. */
function LegacyConsignmentNoteEditRedirect() {
    const { pagemode, id } = useParams<{ pagemode: string; id: string }>();
    return <Navigate to={`${APP_CONST.PATH_APP}/csns/${pagemode}/${id}`} replace />;
}
function LegacyConsignmentNoteListRedirect() {
    return <Navigate to={`${APP_CONST.PATH_APP}/csns`} replace />;
}

/// App
const AppLogin = lazy(() => import('app/authentication/app-login'));
const AppForgotPass = lazy(() => import('app/authentication/app-forgot-password'));
const AppResetPass = lazy(() => import('app/authentication/app-reset-password'));

/// App Components
const AppIndex = lazy(() => import('./app/index'))
const AppHome = lazy(() => import('app/home/home'));
const AppSettingOptions = lazy(() => import('./app/modules/settings/setting-options'));

// Modules

// PO
const AppPurchaseOrder = lazy(() => import('./app/modules/purchase-order/list-view/purchase-order'));
const AppPurchaseOrderEdit = lazy(() => import('./app/modules/purchase-order/edit-view/po-edit'));

// QT
const AppQuotation = lazy(() => import('./app/modules/quotation/list-view/quotation'));
const AppQuotationEdit = lazy(() => import('./app/modules/quotation/edit-view/quotation-edit'));

// Loan Application
const AppLoanApplication = lazy(() => import('./app/modules/loan-application/list-view/loan-application'));
const AppLoanApplicationEdit = lazy(() => import('./app/modules/loan-application/edit-view/loan-application-edit'));

// Loan Disbursement
const AppLoanDisbursement = lazy(() => import('./app/modules/loan-disbursement/list-view/loan-disbursement'));
const AppLoanDisbursementEdit = lazy(() => import('./app/modules/loan-disbursement/edit-view/loan-disbursement-edit'));

// Sales Order
const AppSalesOrder = lazy(() => import('./app/modules/sales-order/list-view/sales-order'));
const AppSalesOrderEdit = lazy(() => import('./app/modules/sales-order/edit-view/sales-order-edit'));

// Invoice
const AppInvoice = lazy(() => import('./app/modules/invoice/list-view/invoice'));
const AppInvoiceEdit = lazy(() => import('./app/modules/invoice/edit-view/invoice-edit'));

// Delivery Order
const AppDeliveryOrder = lazy(() => import('./app/modules/delivery-order/list-view/delivery-order'));
const AppDeliveryOrderEdit = lazy(() => import('./app/modules/delivery-order/edit-view/delivery-order-edit'));

// Customer Payment
const AppCustomerPayment = lazy(() => import('./app/modules/customer-payment/list-view/customer-payment'));
const AppCustomerPaymentEdit = lazy(() => import('./app/modules/customer-payment/edit-view/customer-payment-edit'));

// Customer Refund
const AppCustomerRefund = lazy(() => import('./app/modules/customer-refund/list-view/customer-refund'));
const AppCustomerRefundEdit = lazy(() => import('./app/modules/customer-refund/edit-view/customer-refund-edit'));

const QCWorkOrder = lazy(() => import('./app/modules/qc-workorder/list-view/qc-workorder'));
const QCWorkOrderEdit = lazy(() => import('./app/modules/qc-workorder/edit-view/qc-workorder-edit'));
const QCWorkOrderKanban = lazy(() => import('./app/modules/qc-workorder/kanban/qc-workorder-kanban'));
const QCWorkOrderTracker = lazy(() => import('./app/modules/qc-workorder/tracker-table/qc-workorder-tracker'));
const QCTask = lazy(() => import('./app/modules/qc-workorder-task/list-view/qc-wo-task'));
const QCTaskEdit = lazy(() => import('./app/modules/qc-workorder-task/edit-view/qc-wo-task-edit'));

const AppInspection = lazy(() => import('./app/modules/inspection/list-view/inspection'));
const AppInspectionEdit = lazy(() => import('./app/modules/inspection/edit-view/inspection-edit'));

const AppConsignmentNote = lazy(() => import('./app/modules/consignment-note/list-view/consignment-note'));
const AppConsignmentNoteEdit = lazy(() => import('./app/modules/consignment-note/edit-view/consignment-note-edit'));

const ImportHistory = lazy(() => import('./app/modules/settings/import-history/list-view/import-history'));
const ItemPriceBatchSettings = lazy(() => import('./app/modules/settings/item-price-batch/item-price-batch'));
const ItemPriceBatchDetail = lazy(() => import('./app/modules/settings/item-price-batch/item-price-batch-detail'));
const WooNightlyRefreshList = lazy(() => import('./app/modules/settings/woo-nightly-refresh/woo-nightly-refresh-list'));
const DataMaintenanceHub = lazy(() => import('./app/modules/settings/data-maintenance/data-maintenance-hub'));
const DataMaintenanceTaskPage = lazy(() => import('./app/modules/settings/data-maintenance/data-maintenance-task-page'));
const DataMaintenanceRunDetail = lazy(() => import('./app/modules/settings/data-maintenance/data-maintenance-run-detail'));
const WooNightlyRefreshDetail = lazy(() => import('./app/modules/settings/woo-nightly-refresh/woo-nightly-refresh-detail'));
const NsSyncHistory = lazy(() => import('./app/modules/settings/ns-sync/list-view/ns-sync-history'));
const NsSyncBulk = lazy(() => import('./app/modules/settings/ns-sync/bulk-view/ns-sync-bulk'));
const NsSyncQueue = lazy(() => import('./app/modules/settings/ns-sync/queue-view/ns-sync-queue'));
const ApiHistoryList = lazy(() => import('./app/modules/settings/api-history/list-view/api-history-list'));
const NsQueueList = lazy(() => import('./app/modules/settings/ns-queue/list-view/ns-queue'));

//Master
const AppCustomer = lazy(() => import('./app/modules/customer/list-view/customer'));
const AppCustomerEdit = lazy(() => import('./app/modules/customer/edit-view/customer-edit'));
const AppLead = lazy(() => import('./app/modules/lead/list-view/lead'));

//Vendor
const AppVendor = lazy(() => import('./app/modules/vendor/list-view/vendor'));
const AppVendorEdit = lazy(() => import('./app/modules/vendor/edit-view/vendor-edit'));

// Seller
const AppSeller = lazy(() => import('./app/modules/seller/list-view/seller'));
const AppSellerEdit = lazy(() => import('./app/modules/seller/edit-view/seller-edit'));

//Broker
const AppBroker = lazy(() => import('./app/modules/broker/list-view/broker'));
const AppBrokerEdit = lazy(() => import('./app/modules/broker/edit-view/broker-edit'));

//Supplier
const AppSupplier = lazy(() => import('./app/modules/supplier/list-view/supplier'));
const AppSupplierEdit = lazy(() => import('./app/modules/supplier/edit-view/supplier-edit'));

//Inventory
const AppCar = lazy(() => import('./app/modules/car/list-view/car'));
const AppCarEdit = lazy(() => import('./app/modules/car/edit-view/car-edit'));
const AppCarInventory = lazy(() => import('./app/modules/car-inventory/list-view/car-inventory'));
const AppCarInventoryEdit = lazy(() => import('./app/modules/car-inventory/edit-view/car-inventory-edit'));
const AppCarInventoryCost = lazy(() => import('./app/modules/car-inventory-cost/list-view/car-inventory-cost-list'));
const AppCarInventoryCostEdit = lazy(() => import('./app/modules/car-inventory-cost/edit-view/car-inventory-cost-edit'));
const AppCarInventoryCostPaymentList = lazy(
    () => import('./app/modules/car-inventory-cost-payment/list-view/car-inventory-cost-payment-list')
);

// Payment Request
const AppPaymentRequest = lazy(() => import('./app/modules/payment-request/list-view/payment-request'));
const AppPaymentRequestEdit = lazy(() => import('./app/modules/payment-request/edit-view/prq-edit'));

/// Center Admin
const AppAdminSaleIndex = lazy(() => import('./app/center-admin/index'))

const AppCAHome = lazy(() => import('./app/center-admin/home/home'));

/// POC
const POCIndex = lazy(() => import('./app/poc/Index'));

//User Setting
const AppUserList = lazy(() => import('./app/modules/settings/user/list-view/user'));
const AppUserEdit = lazy(() => import('./app/modules/settings/user/edit-view/user-edit'));
const AppRoleSetting = lazy(() => import('./app/modules/settings/role/role-edit'));
const AppRolesByModule = lazy(() => import('./app/modules/settings/role/roles-by-module'));
const AppApplicationSetting = lazy(() => import('./app/modules/settings/application/application-edit'));
const AppRunningNumberSetting = lazy(() => import('./app/modules/settings/running-number/running-number-edit'));
const AppCustomList = lazy(() => import('./app/modules/settings/custom-list/custom-list-edit'));
const AppCustomRecordOptions = lazy(() => import('./app/modules/settings/custom-record/custom-record-options'));
const AppAPIConfig = lazy(() => import('./app/modules/settings/api-config/api-config-edit'));

const FieldMapping = lazy(() => import('./app/modules/settings/field-mapping/list-view/field-mapping-list'));
const FieldMappingEdit = lazy(() => import('./app/modules/settings/field-mapping/edit-view/field-mapping-edit'));

/// Records
const ShowroomList = lazy(() => import('./app/modules/settings/custom-record/showroom/list-view/showroom'));
const ShowroomEdit = lazy(() => import('./app/modules/settings/custom-record/showroom/edit-view/showroom-edit'));
const LocationList = lazy(() => import('./app/modules/settings/custom-record/location/list-view/location'));
const LocationEdit = lazy(() => import('./app/modules/settings/custom-record/location/edit-view/location-edit'));
const DepartmentList = lazy(() => import('./app/modules/settings/custom-record/department/list-view/department'));
const DepartmentEdit = lazy(() => import('./app/modules/settings/custom-record/department/edit-view/department-edit'));
const BrandList = lazy(() => import('./app/modules/settings/custom-record/brand/list-view/brand'));
const BrandEdit = lazy(() => import('./app/modules/settings/custom-record/brand/edit-view/brand-edit'));
const AdditionalChargeTypeList = lazy(() => import('./app/modules/settings/custom-record/additional-charge-type/list-view/additional-charge-type'));
const AdditionalChargeTypeEdit = lazy(() => import('./app/modules/settings/custom-record/additional-charge-type/edit-view/additional-charge-type-edit'));
const AttachmentTypeList = lazy(() => import('./app/modules/settings/custom-record/attachment-type/list-view/attachment-type'));
const AttachmentTypeEdit = lazy(() => import('./app/modules/settings/custom-record/attachment-type/edit-view/attachment-type-edit'));
const ChecklistTemplateList = lazy(() => import('./app/modules/settings/custom-record/checklist-template/list-view/checklist-template'));
const ChecklistTemplateEdit = lazy(() => import('./app/modules/settings/custom-record/checklist-template/edit-view/checklist-template-edit'));
const BankList = lazy(() => import('./app/modules/settings/custom-record/bank/list-view/bank'));
const BankEdit = lazy(() => import('./app/modules/settings/custom-record/bank/edit-view/bank-edit'));
const CostTypeList = lazy(() => import('./app/modules/settings/custom-record/cost-type/list-view/cost-type'));
const CostTypeEdit = lazy(() => import('./app/modules/settings/custom-record/cost-type/edit-view/cost-type-edit'));
const SalesPrqPayeeCategoryList = lazy(() => import('./app/modules/settings/custom-record/sales-prq-payee-category/list-view/sales-prq-payee-category'));
const SalesPrqPayeeCategoryEdit = lazy(() => import('./app/modules/settings/custom-record/sales-prq-payee-category/edit-view/sales-prq-payee-category-edit'));
const PriceTypeList = lazy(() => import('./app/modules/settings/custom-record/price-type/list-view/price-type'));
const PriceTypeEdit = lazy(() => import('./app/modules/settings/custom-record/price-type/edit-view/price-type-edit'));
const QCWorkstationList = lazy(() => import('./app/modules/settings/custom-record/qc-workstation/list-view/qc-workstation'));
const QCWorkstationEdit = lazy(() => import('./app/modules/settings/custom-record/qc-workstation/edit-view/qc-workstation-edit'));
const QCTaskMaintenanceList = lazy(() => import('./app/modules/settings/custom-record/qc-task/list-view/qc-task'));
const QCTaskMaintenanceEdit = lazy(() => import('./app/modules/settings/custom-record/qc-task/edit-view/qc-task-edit'));
const QCTaskTemplateList = lazy(() => import('./app/modules/settings/custom-record/qc-task-template/list-view/qc-task-template'));
const QCTaskTemplateEdit = lazy(() => import('./app/modules/settings/custom-record/qc-task-template/edit-view/qc-task-template-edit'));
const AccessoryTypeList = lazy(() => import('./app/modules/settings/custom-record/accessory-type/list-view/accessory-type'));
const AccessoryTypeEdit = lazy(() => import('./app/modules/settings/custom-record/accessory-type/edit-view/accessory-type-edit'));
const CompanyBankAccountList = lazy(() => import('./app/modules/settings/custom-record/company-bank-account/list-view/company-bank-account'));
const CompanyBankAccountEdit = lazy(() => import('./app/modules/settings/custom-record/company-bank-account/edit-view/company-bank-account-edit'));
const InsuranceCompanyList = lazy(() => import('./app/modules/settings/custom-record/insurance-company/list-view/insurance-company'));
const InsuranceCompanyEdit = lazy(() => import('./app/modules/settings/custom-record/insurance-company/edit-view/insurance-company-edit'));

// QC Service Line [deprecated]
// const QCServiceLineList = lazy(() => import('./app/modules/settings/qc-service-line/list-view/qc-service-line'));
// const QCServiceLineEdit = lazy(() => import('./app/modules/settings/qc-service-line/edit-view/qc-service-line-edit'));


//Approval Setting
const AppApprovalSetting = lazy(() => import('./app/modules/settings/approval/ApprovalSettingsView'));
const LeadScoringConfigView = lazy(() => import('./app/modules/settings/lead-scoring/LeadScoringConfigView'));
const PeriodClosingSettings = lazy(() => import('./app/modules/settings/period-closing/period-closing'));

const AppApprovalTransaction = lazy(() => import('./app/modules/approval/list-view/approval-transaction'));
const AppApprovalTransactionDetail = lazy(() => import('./app/modules/approval/detail-view/approval-transaction-detail-view'));

// Reports
const AppReportList = lazy(() => import('./app/modules/report/report-list'));
const AppReportDesigner = lazy(() => import('./app/modules/report/report-designer/report-designer'));
const AppReportViewer = lazy(() => import('./app/modules/report/report-viewer/report-viewer'));
const AppDatasourceList = lazy(() => import('./app/modules/report/report-datasource/report-datasource-list'));
const AppDatasourceEdit = lazy(() => import('./app/modules/report/report-datasource/report-datasource-edit'));
const AppConstantList = lazy(() => import('./app/modules/report/report-constant/report-constant-list'));
const AppConstantEdit = lazy(() => import('./app/modules/report/report-constant/report-constant-edit'));
const AppExportReportList = lazy(() => import('./app/modules/report/export-report/export-report-list'));
const AppExportReportEdit = lazy(() => import('./app/modules/report/export-report/export-report-edit'));
const AppExportReportRun = lazy(() => import('./app/modules/report/export-report/export-report-run'));

const DashboardWidgetDefinitionList = lazy(() => import('./app/modules/settings/dashboard-widgets/list-view/dashboard-widget-definition-list'));
const DashboardWidgetDefinitionEdit = lazy(() => import('./app/modules/settings/dashboard-widgets/edit-view/dashboard-widget-definition-edit'));
const DashboardWidgetTemplateList = lazy(() => import('./app/modules/settings/dashboard-widgets/list-view/dashboard-widget-template-list'));
const DashboardWidgetTemplateEdit = lazy(() => import('./app/modules/settings/dashboard-widgets/edit-view/dashboard-widget-template-edit'));

const AppUtilities = lazy(() => import('./app/modules/utilities/list-view/utilities'));
const UtilityAccessSettings = lazy(() => import('./app/modules/settings/utility-access/utility-access'));


// Stock Transfer
const AppStockTransferCar = lazy(() => import('./app/modules/stock-transfer/detail-list-view/stock-transfer-car'));
const AppStockTransferEdit = lazy(() => import('./app/modules/stock-transfer/edit-view/stock-transfer-edit'));

const AppTodo = lazy(() => import('./app/modules/todo/list-view/todo'));
const AppTodoEdit = lazy(() => import('./app/modules/todo/edit-view/todo-edit'));

/// Admin App
const AdminLogin = lazy(() => import('./app-admin/authentication/admin-login'));
const AdminIndex = lazy(() => import('./app-admin/index'));
const AdminDashboard = lazy(() => import('./app-admin/dashboard/dashboard'));
const AdminPortalUserList = lazy(() => import('./app-admin/user/user'));
const AdminPortalUserView = lazy(() => import('./app-admin/user/user-view'));
const AdminPortalUserEdit = lazy(() => import('./app-admin/user/user-edit'));
const AdminPortalUserAccountEdit = lazy(() => import('./app-admin/user/user-account-edit'));
const AdminAccountList = lazy(() => import('./app-admin/account/account'));
const AdminAccountView = lazy(() => import('./app-admin/account/account-view'));
const AdminAccountEdit = lazy(() => import('./app-admin/account/account-edit'));
const AdminSubscriptionView = lazy(() => import('./app-admin/subscription/subscription-view'));
const AdminSubscriptionEdit = lazy(() => import('./app-admin/subscription/subscription-edit'));
const AdminStaffUserList = lazy(() => import('./app-admin/admin-user/admin-user'));
const RouteDefault = (props: any) => {
    const { redirect } = props;

    let userSession: any = localStorage.getItem(APP_CONST.KEY_STORE_USER);

    if (userSession === null || userSession === undefined || userSession === 'undefined') {
        return <Navigate to={redirect} replace />
    }

    userSession = JSON.parse(userSession as 'string');
    if (!userSession) return <Navigate to={redirect} replace />

    return <Navigate to={redirect} replace />
    // alert (JSON.stringify(userSession));
    // if (userSession.defaultCenter) {
    //     switch(userSession.defaultCenter) {
    //         case 'center-sale': return <Navigate to={'/app/sale/'} replace/>
    //         case 'center-admin': return <Navigate to={'/app/admin/'} replace/>
    //     }
    // }

    // return (
    // <h2>
    //     This is not a valid url.
    // </h2>
    // );

}

function RouteDebugger() {
    const location = useLocation();

    useEffect(() => {
        console.group("🔍 ROUTE DEBUG");
        console.log("Current URL:", location.pathname);
        console.groupEnd();
    }, [location]);

    return null;
}

function AppRouter(props: any) {

    useEffect(() => {

        // const userSession = {
        //     exp: Math.floor(new Date().getTime() / 1000) + 60 * 60 * 24,
        //     role: 'Admnistrator',
        //     permissions: [],
        //     centers: ['center-sale', 'center-admin'],
        //     defaultCenter: 'center-sale'
        // }
        // localStorage.setItem('AppUserSession', JSON.stringify(userSession));
    }, []);

    return (
        <Router>
            <RouteDebugger />
            <Suspense fallback={<div>Loading...</div>}>
                <Routes>


                    <Route path="/app/login/:tenantId" element={<AppLogin />} />
                    <Route path="/app/login" element={<AppLogin />} />
                    <Route path="/app/forgotpass" element={<AppForgotPass />} />
                    <Route path={`/app/resetpass`} element={<AppResetPass />} />

                    <Route index element={<RouteDefault redirect="/app/v1/" />} />
                    <Route path="/app" element={<RouteDefault redirect="/app/v1/" />} />
                    <Route path="/poc" element={<POCIndex />} />



                    <Route path="/admin/login" element={<AdminLogin />} />

                    <Route path="/admin" element={<AdminIndex guard={AdminGuard} redirect={'/admin/login'} />}>
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="dashboard" element={<AdminDashboard />} />

                        <Route path="adminusers" element={<AdminStaffUserList />} />

                        <Route path="users/:userId/accounts/:accountId" element={<AdminPortalUserAccountEdit />} />
                        <Route path="users/:id/view" element={<AdminPortalUserView />} />
                        <Route path="users/:id" element={<AdminPortalUserEdit />} />
                        <Route path="users" element={<AdminPortalUserList />} />

                        <Route path="accounts/:id/edit" element={<AdminAccountEdit />} />
                        <Route path="accounts/:id/view" element={<AdminAccountView />} />
                        <Route path="accounts" element={<AdminAccountList />} />

                        <Route path="accounts/:accountId/subscriptions/:subscriptionId/view" element={<AdminSubscriptionView />} />
                        <Route path="accounts/:accountId/subscriptions/:subscriptionId" element={<AdminSubscriptionEdit />} />
                    </Route>
                    <Route path="/app/v1" element={<AppIndex guard={AppGuard} redirect={"/app/login"} />}>
                        <Route index element={<AppHome />} />
                        <Route path="home" element={<AppHome />} />
                        <Route path="settings" element={<AppSettingOptions />} />
                        <Route path="pos" element={<AppPurchaseOrder />} />
                        <Route path="pos/:pagemode/:id" element={<AppPurchaseOrderEdit />} />


                        <Route path="quos/:pagemode/:id" element={<AppQuotationEdit />} />
                        <Route path="quos" element={<AppQuotation />} />
                        
                        <Route path="loanapplications/:pagemode/:id" element={<AppLoanApplicationEdit />} />
                        <Route path="loanapplications" element={<AppLoanApplication />} />
                        
                        <Route path="loandisbursements/:pagemode/:id" element={<AppLoanDisbursementEdit />} />
                        <Route path="loandisbursements" element={<AppLoanDisbursement />} />
                        
                        <Route path="salesorders/:pagemode/:id" element={<AppSalesOrderEdit />} />
                        <Route path="salesorders" element={<AppSalesOrder />} />
                        
                        <Route path="invoices/:pagemode/:id" element={<AppInvoiceEdit />} />
                        <Route path="invoices" element={<AppInvoice />} />

                        <Route path="deliveryorders/:pagemode/:id" element={<AppDeliveryOrderEdit />} />
                        <Route path="deliveryorders" element={<AppDeliveryOrder />} />

                        <Route path="customerpayments/:pagemode/:id" element={<AppCustomerPaymentEdit />} />
                        <Route path="customerpayments" element={<AppCustomerPayment />} />
                        
                        <Route path="customerrefunds/:pagemode/:id" element={<AppCustomerRefundEdit />} />
                        <Route path="customerrefunds" element={<AppCustomerRefund />} />

                        <Route path="customers/:pagemode/:id" element={<AppCustomerEdit />} />
                        <Route path="customers" element={<AppCustomer />} />
                        <Route path="leads/:pagemode/:id" element={<AppCustomerEdit />} />
                        <Route path="leads" element={<AppLead />} />
                        

                        {/* <Route path="sellers" element={<AppSeller />} />
                        <Route path="sellers/:pagemode/:id" element={<AppSellerEdit />} />
                        <Route path="vendors/:pagemode/:id" element={<AppVendorEdit />} />
                        <Route path="vendors" element={<AppVendor />} />
                        <Route path="brokers/:pagemode/:id" element={<AppBrokerEdit />} />
                        <Route path="brokers" element={<AppBroker />} /> */}
                        <Route path="vendors/:pagemode/:id" element={<AppSupplierEdit />} />
                        <Route path="vendors" element={<AppSupplier />} />

                        <Route path="carinventorys/:pagemode/:id" element={<AppCarInventoryEdit />} />
                        <Route path="carinventorys" element={<AppCarInventory />} />
                        <Route path="carinventorycosts/:pagemode/:id" element={<AppCarInventoryCostEdit />} />
                        <Route path="carinventorycosts" element={<AppCarInventoryCost />} />
                        <Route path="carinventorycostpayments" element={<AppCarInventoryCostPaymentList />} />

                        <Route path="prqs" element={<AppPaymentRequest />} />
                        <Route path="prqs/:pagemode/:id" element={<AppPaymentRequestEdit />} />

                        <Route path="qcworkordertasks/:pagemode/:id" element={<QCTaskEdit />} />
                        <Route path="qcworkordertasks" element={<QCTask />} />
                        <Route path="qcworkorders/:pagemode/:id" element={<QCWorkOrderEdit />} />
                        {/* <Route path="qcworkorders/kanban" element={<QCWorkOrderKanban />} /> */}
                        <Route path="qcworkorders/tracker" element={<QCWorkOrderTracker />} />
                        <Route path="qcworkorders" element={<QCWorkOrder />} />
                        
                        <Route path="inspections/:pagemode/:id" element={<AppInspectionEdit />} />
                        <Route path="inspections" element={<AppInspection />} />

                        <Route path="csns/:pagemode/:id" element={<AppConsignmentNoteEdit />} />
                        <Route path="csns" element={<AppConsignmentNote />} />
                        <Route path="consignmentnotes/:pagemode/:id" element={<LegacyConsignmentNoteEditRedirect />} />
                        <Route path="consignmentnotes" element={<LegacyConsignmentNoteListRedirect />} />

                        <Route path='stxs/:pagemode/:id' element={<AppStockTransferEdit />} />
                        <Route path='stxs' element={<AppStockTransferCar />} />

                        <Route path="approval-transaction" element={<AppApprovalTransaction />} />
                        <Route path="approval-transaction/view/:id" element={<AppApprovalTransactionDetail />} />

                        <Route path="todos" element={<AppTodo />} />
                        <Route path="todos/:pagemode/:id" element={<AppTodoEdit />} />

                        <Route path="settings/reports" element={<AppReportList />} />
                        <Route path="settings/reports/design" element={<AppReportDesigner />} />
                        <Route path="settings/reports/design/:id" element={<AppReportDesigner />} />
                        <Route path="settings/reports/view/:id" element={<AppReportViewer />} />
                        <Route path="settings/reports/datasources" element={<AppDatasourceList />} />
                        <Route path="settings/reports/datasources/new" element={<AppDatasourceEdit />} />
                        <Route path="settings/reports/datasources/:id" element={<AppDatasourceEdit />} />
                        <Route path="settings/reports/constants" element={<AppConstantList />} />
                        <Route path="settings/reports/constants/new" element={<AppConstantEdit />} />
                        <Route path="settings/reports/constants/:id" element={<AppConstantEdit />} />
                        <Route path="settings/reports/export-definitions" element={<AppExportReportList />} />
                        <Route path="settings/reports/export-definitions/new" element={<AppExportReportEdit />} />
                        <Route path="settings/reports/export-definitions/:id" element={<AppExportReportEdit />} />
                        <Route path="settings/reports/export-inquiry/:id" element={<AppExportReportRun />} />
                        <Route path="utilities" element={<AppUtilities />} />
                        <Route path="settings/dashboardwidgettemplates/new" element={<DashboardWidgetTemplateEdit />} />
                        <Route path="settings/dashboardwidgettemplates/:id" element={<DashboardWidgetTemplateEdit />} />
                        <Route path="settings/dashboardwidgettemplates" element={<DashboardWidgetTemplateList />} />
                        <Route path="settings/dashboardwidgets/new" element={<DashboardWidgetDefinitionEdit />} />
                        <Route path="settings/dashboardwidgets/:id" element={<DashboardWidgetDefinitionEdit />} />
                        <Route path="settings/dashboardwidgets" element={<DashboardWidgetDefinitionList />} />
                        <Route path="settings/leadscoring" element={<LeadScoringConfigView />} />
                        <Route path="settings/periodclosing" element={<PeriodClosingSettings />} />

                        {/* Setting */}
                        <Route path="settings/appusers/:pagemode/:id" element={<AppUserEdit />} />
                        <Route path="settings/appusers" element={<AppUserList />} />
                        <Route path="settings/roles" element={<AppRoleSetting />} />
                        <Route path="settings/rolesbymodule" element={<AppRolesByModule />} />
                        <Route path="settings/utilityaccess" element={<UtilityAccessSettings />} />
                        <Route path="settings/applications" element={<AppApplicationSetting />} />
                        <Route path="settings/runningnumbers" element={<AppRunningNumberSetting />} />
                        <Route path="settings/approval" element={<AppApprovalSetting />} />
                        <Route path="settings/customlists" element={<AppCustomList />} />
                        <Route path="settings/fieldmappings/edit/:moduleCode" element={<FieldMappingEdit />} />
                        <Route path="settings/fieldmappings" element={<FieldMapping />} />
                        <Route path="settings/apiconfig" element={<AppAPIConfig />} />

                        <Route path="settings/records/showrooms/:pagemode/:id" element={<ShowroomEdit />} />
                        <Route path="settings/records/showrooms" element={<ShowroomList />} />
                        <Route path="settings/records/locations/:pagemode/:id" element={<LocationEdit />} />
                        <Route path="settings/records/locations" element={<LocationList />} />
                        <Route path="settings/records/departments/:pagemode/:id" element={<DepartmentEdit />} />
                        <Route path="settings/records/departments" element={<DepartmentList />} />
                        <Route path="settings/records/brands/:pagemode/:id" element={<BrandEdit />} />
                        <Route path="settings/records/brands" element={<BrandList />} />
                        <Route path="settings/records/additionalchargetypes/:pagemode/:id" element={<AdditionalChargeTypeEdit />} />
                        <Route path="settings/records/additionalchargetypes" element={<AdditionalChargeTypeList />} />
                        <Route path="settings/records/attachmenttypes/:pagemode/:id" element={<AttachmentTypeEdit />} />
                        <Route path="settings/records/attachmenttypes" element={<AttachmentTypeList />} />
                        <Route path="settings/records/checklisttemplates/:pagemode/:id" element={<ChecklistTemplateEdit />} />
                        <Route path="settings/records/checklisttemplates" element={<ChecklistTemplateList />} />
                        <Route path="settings/records/banks/:pagemode/:id" element={<BankEdit />} />
                        <Route path="settings/records/banks" element={<BankList />} />
                        <Route path="settings/records/costtypes/:pagemode/:id" element={<CostTypeEdit />} />
                        <Route path="settings/records/costtypes" element={<CostTypeList />} />
                        <Route path="settings/records/salesprqpayeecategories/:pagemode/:id" element={<SalesPrqPayeeCategoryEdit />} />
                        <Route path="settings/records/salesprqpayeecategories" element={<SalesPrqPayeeCategoryList />} />
                        <Route path="settings/records/pricetype/:pagemode/:id" element={<PriceTypeEdit />} />
                        <Route path="settings/records/pricetype" element={<PriceTypeList />} />
                        <Route path="settings/records/accessorytypes/:pagemode/:id" element={<AccessoryTypeEdit />} />
                        <Route path="settings/records/accessorytypes" element={<AccessoryTypeList />} />
                        <Route path="settings/records/companybankaccounts/:pagemode/:id" element={<CompanyBankAccountEdit />} />
                        <Route path="settings/records/companybankaccounts" element={<CompanyBankAccountList />} />
                        <Route path="settings/records/insurancecompanies/:pagemode/:id" element={<InsuranceCompanyEdit />} />
                        <Route path="settings/records/insurancecompanies" element={<InsuranceCompanyList />} />

                        <Route path="settings/records/qcworkstations/:pagemode/:id" element={<QCWorkstationEdit />} />
                        <Route path="settings/records/qcworkstations" element={<QCWorkstationList />} />
                        <Route path="settings/records/qctasks/:pagemode/:id" element={<QCTaskMaintenanceEdit />} />
                        <Route path="settings/records/qctasks" element={<QCTaskMaintenanceList />} />
                        <Route path="settings/records/qctasktemplates/:pagemode/:id" element={<QCTaskTemplateEdit />} />
                        <Route path="settings/records/qctasktemplates" element={<QCTaskTemplateList />} />
                        <Route path="settings/importhistory" element={<ImportHistory />} />
                        <Route path="settings/itempricebatch/:id" element={<ItemPriceBatchDetail />} />
                        <Route path="settings/itempricebatch" element={<ItemPriceBatchSettings />} />
                        <Route path="settings/woonightlyrefresh/:id" element={<WooNightlyRefreshDetail />} />
                        <Route path="settings/woonightlyrefresh" element={<WooNightlyRefreshList />} />
                        <Route path="settings/datamaintenance/tasks/:taskSegment" element={<RequireAppAdmin module='Data Maintenance'><DataMaintenanceTaskPage /></RequireAppAdmin>} />
                        <Route path="settings/datamaintenance/:id" element={<RequireAppAdmin module='Data Maintenance'><DataMaintenanceRunDetail /></RequireAppAdmin>} />
                        <Route path="settings/datamaintenance" element={<RequireAppAdmin module='Data Maintenance'><DataMaintenanceHub /></RequireAppAdmin>} />
                        <Route path="settings/nssyncbulk" element={<NsSyncBulk />} />
                        <Route path="settings/nssyncqueue" element={<NsSyncQueue />} />
                        <Route path="settings/nssync" element={<NsSyncHistory />} />
                        <Route path="settings/apihistory" element={<ApiHistoryList />} />
                        <Route path="settings/nsqueue" element={<NsQueueList />} />
                        <Route path="settings/records/carprofiles/:pagemode/:id" element={<AppCarEdit />} />
                        <Route path="settings/records/carprofiles" element={<AppCar />} />

                        <Route path="settings/records" element={<AppCustomRecordOptions />} />
                        
                    </Route>
                    <Route path="/app/admin" element={<AppAdminSaleIndex guard={AdminGuard} redirect={"/app/login"} />}>
                        <Route index element={<AppCAHome />} />

                    </Route>

                    <Route path="*" element={<p>Page Not Found: 404!</p>} />
                </Routes>
            </Suspense>
        </Router>
    )

}
export default AppRouter;